import { isPlatformBrowser } from '@angular/common'
import {
  Injectable,
  InjectionToken,
  signal,
  computed,
  effect,
  PLATFORM_ID,
  inject
} from '@angular/core'

const STORAGE_KEY = 'yoshu_theme'

export type Theme = 'light' | 'dark'

/**
 * Theme applied when the user has no stored preference. Defaults to dark
 * (original Safran behaviour); light-first apps override it in their
 * app config: `{ provide: DEFAULT_THEME, useValue: 'light' }`.
 * Keep the index.html boot script's fallback in sync with this value.
 */
export const DEFAULT_THEME = new InjectionToken<Theme>('DEFAULT_THEME', {
  factory: () => 'dark'
})

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly platformId = inject(PLATFORM_ID)
  private readonly isBrowser = isPlatformBrowser(this.platformId)
  private readonly defaultTheme = inject(DEFAULT_THEME)

  /** Current theme. Falls back to DEFAULT_THEME when no preference is stored. */
  readonly theme = signal<Theme>(this.loadPreference())

  readonly isDark = computed(() => this.theme() === 'dark')

  constructor() {
    effect(() => {
      if (!this.isBrowser) return

      const dark = this.theme() === 'dark'
      document.documentElement.classList.toggle('dark', dark)

      // Keep the browser-chrome colour in sync with the active palette. The
      // token holds an HSL triplet (e.g. "220 20% 6%"), valid inside hsl().
      const meta = document.querySelector('meta[name="theme-color"]')
      const bg = getComputedStyle(document.documentElement).getPropertyValue('--background').trim()
      meta?.setAttribute('content', bg ? `hsl(${bg})` : dark ? '#0f1318' : '#ffffff')
    })
  }

  toggle() {
    this.setTheme(this.theme() === 'dark' ? 'light' : 'dark')
  }

  /**
   * The preference is persisted here — on explicit user choice — rather than
   * in an effect, so merely visiting an app never locks its current
   * DEFAULT_THEME into localStorage for users who never picked a theme.
   */
  setTheme(theme: Theme) {
    this.theme.set(theme)
    if (this.isBrowser) this.savePreference(theme)
  }

  private loadPreference(): Theme {
    if (!this.isBrowser) return this.defaultTheme
    const stored = localStorage.getItem(STORAGE_KEY)
    return stored === 'light' || stored === 'dark' ? stored : this.defaultTheme
  }

  private savePreference(theme: Theme) {
    localStorage.setItem(STORAGE_KEY, theme)
  }
}
